import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, CheckCircle2, Clock, XCircle, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useApi } from '../contexts/ApiContext';
import { ApiResponse, NsChange } from '../types';
import { registrarName } from '../lib/registrar-names';

/** While a change is open, re-check the registrar this often. */
const POLL_MS = 20_000;

const STYLES: Record<NsChange['status'], { label: (registrar: string) => string; cls: string; Icon: typeof Clock }> = {
  pending: { label: (r) => `Pending at ${r}`, cls: 'bg-amber-50 text-amber-800 border-amber-200', Icon: Clock },
  stalled: { label: (r) => `Stalled at ${r}`, cls: 'bg-orange-50 text-orange-800 border-orange-200', Icon: AlertTriangle },
  applied: { label: (r) => `Applied at ${r}`, cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: CheckCircle2 },
  failed: { label: () => 'Not applied', cls: 'bg-red-50 text-red-800 border-red-200', Icon: XCircle },
  superseded: { label: () => 'Superseded', cls: 'bg-slate-50 text-slate-600 border-slate-200', Icon: Clock },
};

const ago = (iso: string | null) => (iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : 'never');

/**
 * Confirmation status of a domain's latest nameserver change. Registrars may
 * apply changes asynchronously (Ilkari does), so "accepted" isn't "done": this polls the registrar
 * (via the API) while the change is open and stops once it resolves.
 */
export default function NsChangeStatus({ domainId, change: initial, onResolved }: {
  domainId: string;
  /** A change just returned by a PUT, shown immediately without a fetch. */
  change?: NsChange | null;
  /** Called once when an open change resolves, so the parent can reload the domain. */
  onResolved?: () => void;
}) {
  const { fetchApi } = useApi();
  const [change, setChange] = useState<NsChange | null>(initial ?? null);
  const [checking, setChecking] = useState(false);
  // Held in a ref so a parent's inline callback doesn't re-trigger the fetches.
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;
  const changeRef = useRef<NsChange | null>(change);

  const load = useCallback(async (refresh: boolean) => {
    setChecking(refresh);
    try {
      const res = await fetchApi<ApiResponse<NsChange | null>>(`/domains/${domainId}/nameservers/status${refresh ? '?refresh=1' : ''}`);
      const prev = changeRef.current;
      const next = res.data ?? null;
      changeRef.current = next;
      setChange(next);
      if (prev && !prev.resolved_at && next?.resolved_at && next.id === prev.id) onResolvedRef.current?.();
    } catch { /* non-fatal: keep last known */ } finally {
      setChecking(false);
    }
  }, [fetchApi, domainId]);

  useEffect(() => { if (initial) { changeRef.current = initial; setChange(initial); } }, [initial]);
  useEffect(() => { if (!initial) load(false); }, [load, initial]);

  const open = !!change && !change.resolved_at;
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(t);
  }, [open, load]);

  if (!change) return null;
  const st = STYLES[change.status];
  const registrar = registrarName(change.registrar_type);
  const recent = change.resolved_at && Date.now() - new Date(change.resolved_at).getTime() < 7 * 24 * 3600 * 1000;
  // Only surface old, settled changes when they failed — otherwise it's noise.
  if (!open && !recent && change.status !== 'failed') return null;

  return (
    <div className={`rounded-lg border px-4 py-3 text-sm ${st.cls}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-medium">
          <st.Icon className="h-4 w-4" />
          {st.label(registrar)}
          {change.registrar_status && open && <span className="font-normal opacity-80">· {registrar}: {change.registrar_status}</span>}
        </div>
        {open && (
          <button onClick={() => load(true)} disabled={checking} className="inline-flex items-center gap-1 text-xs font-medium hover:underline disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${checking ? 'animate-spin' : ''}`} /> Check now
          </button>
        )}
      </div>
      <div className="mt-1 text-xs opacity-90 space-y-0.5">
        <div>Requested <span className="font-mono">{change.requested_nameservers.join(', ')}</span> {ago(change.created_at)}.</div>
        {change.message && <div>{change.message}</div>}
        {change.status === 'failed' && change.observed_nameservers && (
          <div>{registrar} currently has <span className="font-mono">{change.observed_nameservers.join(', ') || 'none'}</span>.</div>
        )}
        <div className="opacity-75">
          {open ? `Checked ${ago(change.last_checked_at)} · re-checking automatically` : `Resolved ${ago(change.resolved_at)}`}
        </div>
      </div>
    </div>
  );
}
